// src/pages/Pages.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Spinner, StatusBar, Card, Btn, Badge, Field, Input, Select,
  Table, PageHeader, StatGrid, ConfirmBox, ThreatIntelBanner, TicketRefInput,
} from "../components/UI";
import {
  searchMailboxes, purgeMessages, addToBlockList,
  grantDelegation, revokeDelegation, getActiveDelegations,
  getMailActivityReport, runMessageTrace,
  writeAuditLog, sendAuditEmail, checkThreatIntel, getSharePointListItems,
  getUserByEmail, searchUsersByName, getUserSignInActivity, getAllUsers,
  revokeUserSessions, revokeUserMfaSessions,
  disableUserAccount, enableUserAccount, fullContainUser,
  graphRequest,
} from "../services/graphService";
import {
  exportPurgePDF, exportPurgeCSV,
  exportDelegationPDF,
  exportTracePDF, exportTraceCSV,
  exportAuditLogPDF, exportAuditLogCSV,
  exportExfilCSV,
} from "../services/reportService";

const BtnRow = ({ children }) => (
  <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>{children}</div>
);

const FormGrid = ({ children, cols = 2 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10 }}>{children}</div>
);

const FullField = ({ label, children }) => (
  <div style={{ gridColumn: "1 / -1" }}>
    <Field label={label}>{children}</Field>
  </div>
);

// ─── PHISHING PURGE ───────────────────────────────────────────────────────────
export function PurgePage({ user, onScanUpdate }) {
  const [form, setForm] = useState({ sender: "", subject: "", msgId: "", keywords: "", dateFrom: "", dateTo: "" });
  const [ticketRef, setTicketRef] = useState("");
  const [phase, setPhase] = useState("idle");
  const [results, setResults] = useState([]);
  const [purgeResults, setPurgeResults] = useState([]);
  const [threatMatches, setThreatMatches] = useState([]);
  const [threatDismissed, setThreatDismissed] = useState(false);
  const [error, setError] = useState("");

  const filled = Object.values(form).filter((v) => v.trim()).length;
  const canSearch = filled >= 3;
  const pct = Math.min((filled / 3) * 100, 100);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function doSearch() {
    setPhase("searching");
    setError("");
    if (onScanUpdate) onScanUpdate("purge", {
      running: true,
      label: `Purge search: ${form.sender || form.subject || "criteria set"}`,
    });
    try {
      const threats = await checkThreatIntel(form.sender, form.sender?.split("@")[1], form.subject);
      setThreatMatches(threats);
      setThreatDismissed(false);

      const res = await searchMailboxes(form);
      setResults(res);
      setPhase("results");
    } catch (err) {
      setError(err.message);
      setPhase("idle");
    } finally {
      if (onScanUpdate) onScanUpdate("purge", null);
    }
  }

  async function doPurge() {
    setPhase("purging");
    if (onScanUpdate) onScanUpdate("purge", { running: true, label: "Purging matched emails…" });
    try {
      const res = await purgeMessages(results);
      setPurgeResults(res);

      const ok = res.filter((r) => r.status === "deleted").length;
      const fail = res.filter((r) => r.status === "failed").length;

      // Log to SharePoint + send email
      const entry = {
        module: "Purge",
        action: `${ok} emails purged — sender: ${form.sender || "N/A"}, subject: ${form.subject || "N/A"}`,
        operator: user?.displayName,
        operatorEmail: user?.mail,
        ticketRef,
        details: { ...form, matchCount: res.length },
        matchCount: res.length,
        successCount: ok,
        failCount: fail,
        severity: fail > 0 ? "warn" : "ok",
      };
      await Promise.all([writeAuditLog(entry), sendAuditEmail(entry)]);
      setPhase("done");
    } catch (err) {
      setError(err.message);
      setPhase("results");
    } finally {
      if (onScanUpdate) onScanUpdate("purge", null);
    }
  }

  async function doRetry() {
    const failed = purgeResults.filter((r) => r.status === "failed");
    const retried = await purgeMessages(failed);
    setPurgeResults((prev) =>
      prev.map((r) => {
        const retry = retried.find((x) => x.messageId === r.messageId);
        return retry || r;
      })
    );
  }

  async function doBlock() {
    if (form.sender) await addToBlockList(form.sender, form.sender.includes("@") ? "sender" : "domain");
    alert(`${form.sender} added to tenant block list.`);
  }

  function reset() {
    setForm({ sender: "", subject: "", msgId: "", keywords: "", dateFrom: "", dateTo: "" });
    setPhase("idle"); setResults([]); setPurgeResults([]); setTicketRef(""); setError("");
  }

  const okCount = purgeResults.filter((r) => r.status === "deleted").length;
  const failCount = purgeResults.filter((r) => r.status === "failed").length;

  return (
    <div className="fade-in">
      <PageHeader
        title="Phishing purge"
        sub="Search and permanently delete confirmed phishing emails across all org mailboxes"
        actions={phase !== "idle" && <Btn onClick={reset}>Reset</Btn>}
      />

      {!threatDismissed && (
        <ThreatIntelBanner matches={threatMatches} onDismiss={() => setThreatDismissed(true)} />
      )}

      <Card title="Search criteria" action={<span style={{ color: "var(--tx3)" }}>{filled}/5 fields</span>}>
        <FormGrid>
          <Field label="Sender address"><Input value={form.sender} onChange={set("sender")} placeholder="attacker@domain.com" /></Field>
          <Field label="Subject line"><Input value={form.subject} onChange={set("subject")} placeholder="exact or partial match" /></Field>
          <Field label="Message ID"><Input value={form.msgId} onChange={set("msgId")} placeholder="<id@domain.com>" /></Field>
          <Field label="Body keywords"><Input value={form.keywords} onChange={set("keywords")} placeholder="comma-separated keywords" /></Field>
          <Field label="Date received from"><Input type="date" value={form.dateFrom} onChange={set("dateFrom")} /></Field>
          <Field label="Date received to"><Input type="date" value={form.dateTo} onChange={set("dateTo")} /></Field>
          <FullField label="HaloITSM ticket ref (optional)">
            <Input value={ticketRef} onChange={(e) => setTicketRef(e.target.value)} placeholder="e.g. INC-2024-047" />
          </FullField>
        </FormGrid>

        {/* Progress bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 11, color: "var(--tx3)" }}>
          <span>{filled} of 3 minimum</span>
          <div style={{ flex: 1, height: 2, background: "var(--b2)", borderRadius: 1, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 1, transition: "all .3s",
              width: pct + "%",
              background: canSearch ? "var(--green)" : filled >= 2 ? "var(--warn)" : "var(--orange)",
            }} />
          </div>
          <span style={{ color: canSearch ? "var(--green)" : "var(--tx3)" }}>{canSearch ? "ready" : `need ${3 - filled} more`}</span>
        </div>

        {error && <StatusBar type="err" style={{ marginTop: 10 }}>{error}</StatusBar>}

        <BtnRow>
          <Btn variant="orange" onClick={doSearch} disabled={!canSearch || phase === "searching" || phase === "purging"}>
            {(phase === "searching" || phase === "purging") && <Spinner size={10} />}
            {phase === "searching" ? "Searching all mailboxes… (60-120 secs)" :
             phase === "purging" ? "Purging emails… (safe to switch tabs)" :
             "Search all mailboxes"}
          </Btn>
          {(phase === "searching" || phase === "purging") && (
            <span style={{ fontSize:11, color:"var(--primary)", fontFamily:"var(--mono)" }}>
              Running in background — you can switch tabs
            </span>
          )}
        </BtnRow>
        {/* Persistent status shown on ALL tabs while search runs */}
        {phase === "searching" && (
          <div style={{
            marginTop:12, padding:"10px 12px",
            background:"var(--primary-dim)", border:"1px solid rgba(0,178,227,0.25)",
            borderRadius:"var(--r)", fontSize:11, color:"var(--primary)",
            fontFamily:"var(--mono)", display:"flex", alignItems:"center", gap:8,
          }}>
            <span style={{
              width:10, height:10, border:"1.5px solid var(--primary)",
              borderTopColor:"transparent", borderRadius:"50%",
              animation:"spin .7s linear infinite", flexShrink:0,
            }}/>
            Searching all mailboxes for: {form.sender || form.subject || "criteria set"} — this may take a moment
          </div>
        )}
      </Card>

      {phase === "results" && results.length === 0 && (
        <StatusBar type="idle">No matching emails found across org mailboxes.</StatusBar>
      )}

      {phase === "results" && results.length > 0 && (
        <>
          <StatusBar type="ok">{results.length} matching emails found across {new Set(results.map((r) => r.userId)).size} mailboxes</StatusBar>
          <Card title="Match results — review before purging">
            <div style={{ overflowX: "auto" }}>
              <Table
                cols={["Mailbox", "Subject", "Sender", "Received"]}
                rows={results.map((r) => ({
                  cells: [
                    r.userEmail,
                    r.subject?.substring(0, 45),
                    r.sender,
                    new Date(r.received).toLocaleString(),
                  ],
                }))}
              />
            </div>
            <ConfirmBox
              title="Confirm permanent deletion"
              body={`This will permanently delete ${results.length} emails from ${new Set(results.map((r) => r.userId)).size} mailboxes. This cannot be undone. An audit entry will be written to SharePoint (ITSec) and sent to imait@theimagroup.com.`}
              onConfirm={doPurge}
              confirmLabel="Confirm and purge all"
              loading={phase === "purging"}
            />
          </Card>
        </>
      )}

      {phase === "done" && (
        <Card title="Purge results">
          <div style={{ overflowX: "auto" }}>
            <Table
              cols={["Mailbox", "Subject", "Result", "Error"]}
              rows={purgeResults.map((r) => ({
                cells: [
                  r.userEmail,
                  r.subject?.substring(0, 40),
                  <Badge type={r.status === "deleted" ? "ok" : "fail"}>{r.status}</Badge>,
                  r.error || "—",
                ],
              }))}
            />
          </div>
          {failCount > 0 && (
            <div style={{ marginTop: 12 }}>
              <StatusBar type="warn">{failCount} mailbox{failCount > 1 ? "es" : ""} failed — retry below</StatusBar>
              <BtnRow>
                <Btn variant="warn" onClick={doRetry}>Retry {failCount} failed</Btn>
              </BtnRow>
            </div>
          )}
          {failCount === 0 && (
            <StatusBar type="ok">All {okCount} emails permanently deleted. Audit logged to SharePoint and sent to imait@theimagroup.com.</StatusBar>
          )}
          <BtnRow>
            <Btn variant="ghost" onClick={() => exportPurgePDF({ results: purgeResults, criteria: form, operator: user?.mail, ticketRef })}>Export PDF</Btn>
            <Btn variant="ghost" onClick={() => exportPurgeCSV({ results: purgeResults, criteria: form, operator: user?.mail, ticketRef })}>Export CSV</Btn>
            {form.sender && <Btn variant="warn" onClick={doBlock}>Block {form.sender}</Btn>}
            <Btn variant="ghost" onClick={reset}>New purge</Btn>
          </BtnRow>
        </Card>
      )}
    </div>
  );
}

// ─── MAILBOX DELEGATION ───────────────────────────────────────────────────────
export function DelegatePage({ user, onScanUpdate }) {
  const [mailbox, setMailbox] = useState("");
  const [hours, setHours] = useState("6");
  const [reason, setReason] = useState("");
  const [ticketRef, setTicketRef] = useState("");
  const [phase, setPhase] = useState("idle");
  const [active, setActive] = useState([]);
  const [grantResult, setGrantResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getActiveDelegations().then(setActive).catch(() => {});
  }, []);

  async function doGrant() {
    setPhase("granting");
    setError("");
    try {
      const result = await grantDelegation(
        mailbox,
        user?.mail,
        parseInt(hours),
        "rw",
        reason,
        ticketRef
      );
      setGrantResult({ ...result, hours, reason });

      const entry = {
        module: "Delegation",
        action: `Full mailbox access granted — ${mailbox} → ${user?.mail} for ${hours}h`,
        operator: user?.displayName,
        operatorEmail: user?.mail,
        ticketRef,
        details: { mailbox, hours, reason, expiry: result.expiry },
        severity: "info",
      };
      await Promise.all([writeAuditLog(entry), sendAuditEmail(entry)]);

      setActive((a) => [...a, {
        id: Date.now(), mailbox, grantedTo: user?.mail,
        level: "Full access", hours, expiry: result.expiry,
        status: "active", ticketRef,
      }]);
      setPhase("done");
    } catch (err) {
      setError(err.message);
      setPhase("idle");
    }
  }

  async function doRevoke(delegation) {
    try {
      await revokeDelegation(delegation.mailbox, user?.mail, user?.mail, delegation.ticketRef);
      setActive((a) => a.map((d) => d.id === delegation.id ? { ...d, status: "revoked" } : d));

      const entry = {
        module: "Delegation",
        action: `Access revoked — ${delegation.mailbox}`,
        operator: user?.displayName,
        operatorEmail: user?.mail,
        ticketRef: delegation.ticketRef,
        details: { mailbox: delegation.mailbox },
        severity: "info",
      };
      await Promise.all([writeAuditLog(entry), sendAuditEmail(entry)]);
    } catch (err) {
      alert("Revoke failed: " + err.message);
    }
  }

  return (
    <div className="fade-in">
      <PageHeader title="Mailbox delegation" sub="Log and track investigation access to user mailboxes. Actual Exchange permissions granted via admin center." />
      <StatGrid stats={[
        { value: active.filter((d) => d.status === "active").length, label: "Active delegations", color: "var(--teal)" },
        { value: "6h", label: "Default duration" },
        { value: "24h", label: "Max duration" },
        { value: active.filter((d) => d.status !== "active").length, label: "Closed today", color: "var(--tx3)" },
      ]} />

      {/* Exchange note */}
      <div style={{
        background: "var(--primary-dim)", border: "1px solid rgba(0,178,227,0.25)",
        borderRadius: "var(--r)", padding: "10px 14px", marginBottom: 14,
        fontSize: 11, color: "var(--primary)",
      }}>
        <strong>Note:</strong> Full mailbox access requires Exchange Admin Center or PowerShell (<code>Add-MailboxPermission</code>).
        Use this module to log and track investigation access for compliance. The auto-revoke timer will remind you to close access at expiry.
        <span
          onClick={() => window.open("https://admin.exchange.microsoft.com", "_blank")}
          style={{ marginLeft: 10, textDecoration: "underline", cursor: "pointer" }}
        >
          Open Exchange Admin Center →
        </span>
      </div>

      <Card title="Log delegation">
        <FormGrid>
          <FullField label="Target mailbox">
            <Input value={mailbox} onChange={(e) => setMailbox(e.target.value)} placeholder="user@theimagroup.com" />
          </FullField>
          <Field label="Access level">
            <Select value="rw">
              <option value="rw">Read / Write (investigation)</option>
              <option value="ro">Read only</option>
            </Select>
          </Field>
          <Field label="Duration">
            <Select value={hours} onChange={(e) => setHours(e.target.value)}>
              {["1", "2", "4", "6", "8", "12", "24"].map((h) => (
                <option key={h} value={h}>{h} hour{h !== "1" ? "s" : ""}</option>
              ))}
            </Select>
          </Field>
          <FullField label="Reason / incident reference">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Investigating suspected compromise" />
          </FullField>
          <FullField label="HaloITSM ticket ref (optional)">
            <Input value={ticketRef} onChange={(e) => setTicketRef(e.target.value)} placeholder="e.g. INC-2024-047" />
          </FullField>
        </FormGrid>
        {error && <StatusBar type="err">{error}</StatusBar>}
        <BtnRow>
          <Btn variant="blue" onClick={doGrant} disabled={!mailbox || !reason || phase === "granting"}>
            {phase === "granting" && <Spinner size={10} />}
            {phase === "granting" ? "Logging…" : "Log delegation"}
          </Btn>
          {grantResult && (
            <Btn variant="ghost" onClick={() => exportDelegationPDF({ delegation: grantResult, operator: user?.mail, ticketRef })}>
              Export PDF
            </Btn>
          )}
        </BtnRow>
        {phase === "done" && grantResult && (
          <div>
            <StatusBar type="ok">
              Access granted for {mailbox} — {hours} hours. Audit entry written to SharePoint and sent to imait@theimagroup.com.
            </StatusBar>
            {grantResult.owaLink && (
              <div style={{
                marginTop: 8, padding: "10px 14px",
                background: "var(--primary-dim)", border: "1px solid rgba(0,178,227,.25)",
                borderRadius: "var(--r)", fontSize: 11,
              }}>
                <strong style={{ color: "var(--primary)" }}>Open mailbox in Outlook Web:</strong>{" "}
                <a href={grantResult.owaLink} target="_blank" rel="noopener noreferrer"
                  style={{ color: "var(--primary)", wordBreak: "break-all" }}>
                  {grantResult.owaLink}
                </a>
                {!grantResult.fullAccessGranted && (
                  <div style={{ marginTop: 6, color: "var(--tx3)" }}>
                    For full FullAccess delegation, also grant via{" "}
                    <span
                      onClick={() => window.open("https://admin.exchange.microsoft.com", "_blank")}
                      style={{ color: "var(--primary)", cursor: "pointer", textDecoration: "underline" }}
                    >Exchange Admin Center</span>.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Active delegations" noPad>
        <Table
          cols={["Mailbox", "Granted to", "Duration", "Expires", "Ticket ref", "Status", ""]}
          rows={active.map((d) => ({
            cells: [
              d.mailbox,
              d.grantedTo,
              `${d.hours}h`,
              d.expiry ? new Date(d.expiry).toLocaleString() : "—",
              d.ticketRef || "—",
              <Badge type={d.status === "active" ? "active" : "fail"}>{d.status}</Badge>,
              d.status === "active"
                ? <Btn variant="ghost" small onClick={() => doRevoke(d)}>Revoke</Btn>
                : null,
            ],
          }))}
        />
      </Card>
    </div>
  );
}

// ─── EXFILTRATION AUDIT ───────────────────────────────────────────────────────
export function ExfilPage({ user, onScanUpdate }) {
  const [targetUser, setTargetUser] = useState("");
  const [range, setRange] = useState("1");
  const [scanning, setScanning] = useState(false);
  const [flags, setFlags] = useState([]);
  const [scanned, setScanned] = useState(false);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [expanded, setExpanded] = useState(null);

  async function doScan() {
    if (!targetUser) return;
    setScanning(true);
    setScanned(false);
    const isOrgWide = targetUser.toLowerCase() === "all";
    const scanLabel = isOrgWide ? "Org-wide exfil scan (all mailboxes)" : `Exfil scan: ${targetUser}`;
    if (onScanUpdate) onScanUpdate("exfil", { running: true, label: scanLabel });
    try {
      const { flags: f, messages } = await getMailActivityReport(targetUser, parseInt(range));
      // Enrich flags with full message detail
      const enriched = f.map((flag, i) => ({
        id: i + 1,
        severity: flag.severity,
        user: targetUser,
        type: flag.type,
        subject: flag.msg?.subject,
        recipients: flag.recipients || [],
        count: flag.count,
        sentTime: flag.msg?.sentDateTime,
        hasAttachments: flag.msg?.hasAttachments,
        event: buildEventDescription(flag),
      }));
      setFlags(enriched);
      setLiveAlerts(enriched);
      setScanned(true);
    } catch (err) {
      setFlags([]);
      setScanned(true);
    } finally {
      setScanning(false);
      if (onScanUpdate) onScanUpdate("exfil", null);
    }
  }

  function buildEventDescription(flag) {
    switch (flag.type) {
      case "personal_domain": return `Email sent to personal domain — ${flag.recipients?.[0] || "external"}`;
      case "bulk_external": return `${flag.count || "Multiple"} external recipients on a single email`;
      case "attachment_external": return `Email with attachment sent to ${flag.recipients?.length || "external"} external address(es)`;
      default: return flag.type?.replace(/_/g, " ");
    }
  }

  const sevColor = (s) => s === "high" ? "var(--red)" : "var(--warn)";

  return (
    <div className="fade-in">
      <PageHeader title="Exfiltration audit" sub="Detect suspicious outbound email activity. Results below are real data from the scanned mailbox." />
      <StatGrid stats={[
        { value: liveAlerts.filter(a => a.severity === "high").length || 0, label: "High severity", color: "var(--red)" },
        { value: liveAlerts.filter(a => a.severity === "medium").length || 0, label: "Medium severity", color: "var(--warn)" },
        { value: liveAlerts.length, label: "Total flags found" },
        { value: scanned ? "Complete" : scanning ? "Scanning" : "Ready", label: "Scan status", color: scanning ? "var(--primary)" : scanned ? "var(--green)" : "var(--tx3)" },
      ]} />

      <Card title="Scan mailbox(es)">
        <FormGrid>
          <Field label="Target mailbox">
            <Input value={targetUser} onChange={(e) => setTargetUser(e.target.value)} placeholder="user@theimagroup.com — or type 'all' for org-wide scan" />
          </Field>
          <Field label="Time range">
            <Select value={range} onChange={(e) => setRange(e.target.value)}>
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </Select>
          </Field>
        </FormGrid>
        <div style={{ fontSize:11, color:"var(--tx3)", marginTop:8 }}>
          Type a specific email to scan one user, or type <strong style={{color:"var(--primary)"}}>all</strong> to scan every mailbox in the org (slower — may take a few minutes).
        </div>
        <BtnRow>
          <Btn variant="green" onClick={doScan} disabled={!targetUser || scanning}>
            {scanning && <Spinner size={10} />}
            {scanning ? `Scanning ${targetUser}… (safe to switch tabs)` : "Run exfil scan"}
          </Btn>
          {scanning && <span style={{ fontSize:11, color:"var(--primary)", fontFamily:"var(--mono)" }}>
            Running in background — you can switch tabs
          </span>}
        </BtnRow>
      </Card>

      {scanned && liveAlerts.length === 0 && (
        <StatusBar type="ok">No exfiltration signals detected for {targetUser} in the selected period.</StatusBar>
      )}

      {liveAlerts.length > 0 && (
        <Card
          title={`Scan results — ${targetUser}`}
          action={<Btn variant="ghost" small onClick={() => exportExfilCSV(liveAlerts)}>Export CSV</Btn>}
        >
          {liveAlerts.map((a) => (
            <div key={a.id}>
              <div
                onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "12px 0", borderBottom: "1px solid var(--b1)",
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: "var(--r)", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: a.severity === "high" ? "var(--red-dim)" : "var(--warn-dim)",
                  fontSize: 9, fontWeight: 700, color: sevColor(a.severity),
                }}>
                  {a.severity === "high" ? "HIGH" : "MED"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "var(--tx)", fontWeight: 500, marginBottom: 3 }}>{a.event}</div>
                  <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                    {a.type?.replace(/_/g, " ")}
                    {a.sentTime && ` · ${new Date(a.sentTime).toLocaleString()}`}
                    {a.hasAttachments && " · Has attachments"}
                  </div>
                </div>
                <span style={{ fontSize:10, color:"var(--tx3)", marginTop:2 }}>{expanded===a.id ? "▲ less" : "▼ details"}</span>
              </div>
              {/* Expanded detail */}
              {expanded === a.id && (
                <div style={{
                  padding: "12px 14px", margin: "0 0 8px 40px",
                  background: "var(--s2)", borderRadius: "var(--r)",
                  border: "1px solid var(--b1)", fontSize: 11,
                }}>
                  {a.subject && <div style={{ marginBottom: 6 }}><span style={{ color:"var(--tx3)" }}>Subject: </span><span style={{ color:"var(--tx)" }}>{a.subject}</span></div>}
                  {a.recipients?.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ color:"var(--tx3)", marginBottom:4 }}>Recipients ({a.recipients.length}):</div>
                      {a.recipients.map((r, i) => (
                        <div key={i} style={{ color:"var(--tx2)", padding:"2px 0", fontFamily:"var(--mono)", fontSize:10 }}>{r}</div>
                      ))}
                    </div>
                  )}
                  {a.count && <div><span style={{ color:"var(--tx3)" }}>External recipient count: </span><span style={{ color:"var(--red)", fontWeight:600 }}>{a.count}</span></div>}
                  <div style={{ marginTop:10, display:"flex", gap:8 }}>
                    <Btn variant="ghost" small>Open in Outlook</Btn>
                    <Btn variant="ghost" small>Export detail</Btn>
                  </div>
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── MESSAGE TRACE ────────────────────────────────────────────────────────────
export function TracePage({ user, onScanUpdate }) {
  const [form, setForm] = useState({ sender: "", recipient: "", subject: "", dateFrom: "", dateTo: "" });
  const [phase, setPhase] = useState("idle");
  const [results, setResults] = useState([]);
  const [hops, setHops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canTrace = form.sender || form.recipient || form.subject;

  async function doTrace() {
    setPhase("tracing");
    setError("");
    setResults([]);
    setHops([]);
    setSelected(null);
    if (onScanUpdate) onScanUpdate("trace", { running: true, label: "Message trace running" });
    try {
      const messages = await runMessageTrace(form);
      if (!Array.isArray(messages) || messages.length === 0) {
        setResults([]);
        setPhase("done");
        return;
      }
      setResults(messages);
      setPhase("done");
    } catch (err) {
      setError(err.message);
      setPhase("idle");
    } finally {
      if (onScanUpdate) onScanUpdate("trace", null);
    }
  }

  const hopColor = (hop, total) => {
    if (hop.event?.toLowerCase().includes("forward")) return "var(--red)";
    if (hop.event?.toLowerCase().includes("delivered")) return "var(--green)";
    return "var(--purple)";
  };

  return (
    <div className="fade-in">
      <PageHeader title="Message trace" sub="Full SMTP trace including delivery hops, forwarding rules, and spam filter decisions." />
      <Card title="Trace criteria">
        <FormGrid>
          <Field label="Sender"><Input value={form.sender} onChange={set("sender")} placeholder="sender@domain.com" /></Field>
          <Field label="Recipient"><Input value={form.recipient} onChange={set("recipient")} placeholder="user@theimagroup.com" /></Field>
          <FullField label="Subject line"><Input value={form.subject} onChange={set("subject")} placeholder="partial match supported" /></FullField>
          <Field label="Date from"><Input type="date" value={form.dateFrom} onChange={set("dateFrom")} /></Field>
          <Field label="Date to"><Input type="date" value={form.dateTo} onChange={set("dateTo")} /></Field>
        </FormGrid>
        {error && <StatusBar type="err">{error}</StatusBar>}
        <BtnRow>
          <Btn variant="purple" onClick={doTrace} disabled={!canTrace || phase === "tracing"}>
            {phase === "tracing" && <Spinner size={10} />}
            {phase === "tracing" ? "Running Exchange trace… (30-90 secs)" : "Run message trace"}
          </Btn>
          {phase === "tracing" && (
            <span style={{ fontSize:11, color:"var(--purple)", fontFamily:"var(--mono)" }}>
              Running Get-MessageTrace via Runbook — polling for results
            </span>
          )}
        </BtnRow>
      </Card>

      {phase === "done" && results.length === 0 && (
        <StatusBar type="idle">
          No messages found matching those criteria. Try a broader date range or check the sender address.
        </StatusBar>
      )}

      {phase === "done" && results.length > 0 && (
        <>
          <StatusBar type="purple">{results.length} message{results.length > 1 ? "s" : ""} found via Exchange trace</StatusBar>
          <Card
            title="Message trace results"
            action={
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="ghost" small onClick={() => exportTraceCSV(results, form)}>Export CSV</Btn>
                <Btn variant="ghost" small onClick={() => exportTracePDF({ hops: results.map((r,i) => ({ event: r.Status, detail: `${r.SenderAddress || r.sender} → ${r.RecipientAddress || r.recipient}`, time: r.Received || r.received })), criteria: form, operator: user?.mail })}>Export PDF</Btn>
              </div>
            }
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Received", "From", "To", "Subject", "Status", "From IP"].map(h => (
                      <th key={h} style={{ textAlign:"left", fontSize:10, color:"var(--tx3)", padding:"7px 10px", borderBottom:"1px solid var(--b1)", letterSpacing:".06em", textTransform:"uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => {
                    const status = r.Status || r.status || "";
                    const statusColor = status === "Delivered" ? "var(--green)" : status === "Failed" ? "var(--red)" : "var(--warn)";
                    return (
                      <tr key={i} style={{ cursor:"default" }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding:"8px 10px", fontSize:11, color:"var(--tx2)", borderBottom:"1px solid var(--b1)", whiteSpace:"nowrap" }}>
                          {(() => {
                            const raw = r.Received || r.received;
                            if (!raw) return "—";
                            // Handle .NET ticks format: /Date(1234567890123)/
                            const tickMatch = String(raw).match(/\/Date\((\d+)\)\//);
                            if (tickMatch) return new Date(parseInt(tickMatch[1])).toLocaleString();
                            const d = new Date(raw);
                            return isNaN(d) ? raw : d.toLocaleString();
                          })()}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:11, color:"var(--tx2)", borderBottom:"1px solid var(--b1)" }}>
                          {r.SenderAddress || r.sender}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:11, color:"var(--tx2)", borderBottom:"1px solid var(--b1)" }}>
                          {r.RecipientAddress || r.recipient}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:11, color:"var(--tx2)", borderBottom:"1px solid var(--b1)", maxWidth:300, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {r.Subject || r.subject}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:11, borderBottom:"1px solid var(--b1)" }}>
                          <span style={{ color: statusColor, fontWeight:500 }}>{status}</span>
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:11, color:"var(--tx3)", borderBottom:"1px solid var(--b1)", fontFamily:"var(--mono)" }}>
                          {r.FromIP || r.fromIP || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Legacy hop view for Graph-based results */}
      {phase === "done" && hops.length > 0 && results.length === 0 && (
        <>
          <StatusBar type="purple">{hops.length} delivery events found</StatusBar>
          <Card
            title="SMTP delivery trace"
            action={
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="ghost" small onClick={() => exportTracePDF({ hops, criteria: form, operator: user?.mail })}>PDF</Btn>
                <Btn variant="ghost" small onClick={() => exportTraceCSV(hops, form)}>CSV</Btn>
              </div>
            }
          >
            {hops.map((hop, i) => (
              <div key={i} style={{ display: "flex", alignItems: "stretch", marginBottom: 8 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 28, flexShrink: 0 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    border: `2px solid ${hopColor(hop, hops.length)}`,
                    background: "var(--bg)", flexShrink: 0,
                  }} />
                  {i < hops.length - 1 && <div style={{ flex: 1, width: 1, background: "var(--b2)", margin: "3px 0" }} />}
                </div>
                <div style={{
                  flex: 1, background: "var(--s2)", border: "1px solid var(--b1)",
                  borderRadius: "var(--r)", padding: "8px 12px", marginLeft: 8,
                }}>
                  <div style={{ fontSize: 12, color: hopColor(hop, hops.length), fontWeight: 500, marginBottom: 2 }}>{hop.event}</div>
                  <div style={{ fontSize: 11, color: "var(--tx2)" }}>{hop.detail}</div>
                  <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 2 }}>{hop.time}</div>
                </div>
              </div>
            ))}
            {hops.some((h) => h.event?.toLowerCase().includes("forward")) && (
              <StatusBar type="err">Auto-forward rule detected — consider disabling forwarding on this mailbox</StatusBar>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ─── THREAT INTELLIGENCE ──────────────────────────────────────────────────────
export function ThreatPage({ user, onScanUpdate }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searched, setSearched] = useState(false);

  async function doSearch() {
    if (!query) return;
    setLoading(true);
    const isDomain = !query.includes("@") && query.includes(".");
    const isSender = query.includes("@");
    const res = await checkThreatIntel(
      isSender ? query : null,
      isDomain ? query : null,
      !isSender && !isDomain ? query : null
    );
    setMatches(res);
    setSearched(true);
    setLoading(false);
  }

  return (
    <div className="fade-in">
      <PageHeader title="Threat intelligence" sub="Cross-reference sender addresses, domains, or subject lines against historical incidents." />
      <Card title="Search historical incidents">
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="sender@domain.com · domain.com · or subject line keyword"
            />
          </div>
          <Btn variant="warn" onClick={doSearch} disabled={!query || loading}>
            {loading && <Spinner size={10} />}
            Search
          </Btn>
        </div>
        <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 6 }}>
          Enter a sender email, domain, or subject line keyword to check against all previous purge operations.
        </div>
      </Card>

      {searched && (
        matches.length === 0
          ? <StatusBar type="ok">No historical matches found for "{query}".</StatusBar>
          : (
              <Card title={`${matches.length} historical match${matches.length > 1 ? "es" : ""} found`}>
                <Table
                  cols={["Date", "Action", "Operator", "Ticket ref", "Count"]}
                  rows={matches.map((m) => ({
                    cells: [
                      m.date?.substring(0, 10),
                      m.action,
                      m.operator,
                      m.ticketRef || "—",
                      m.count !== undefined ? <Badge type="warn">{m.count} emails</Badge> : "—",
                    ],
                  }))}
                />
                <StatusBar type="warn">
                  This sender / domain has been seen in previous incidents. Proceed with awareness.
                </StatusBar>
              </Card>
            )
      )}
    </div>
  );
}

// ─── AUDIT LOG ────────────────────────────────────────────────────────────────

// Module-level in-memory log — persists across tab switches within the session
// Written to by writeAuditLog in graphService, read here
export const localAuditLog = [];

export function LogPage({ user, onScanUpdate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [spError, setSpError] = useState(false);

  function loadLog() {
    setLoading(true);
    getSharePointListItems("PhishingResponseAudit", "$orderby=fields/Timestamp desc&$top=100")
      .then((items) => {
        const spEntries = items.map((i) => ({
          timestamp: i.fields?.Timestamp ? new Date(i.fields.Timestamp).toLocaleString() : "—",
          module: i.fields?.Module,
          action: i.fields?.Title,
          operatorEmail: i.fields?.OperatorEmail,
          ticketRef: i.fields?.TicketRef,
          matchCount: i.fields?.MatchCount,
          successCount: i.fields?.SuccessCount,
          failCount: i.fields?.FailCount,
          severity: i.fields?.Severity,
          source: "sharepoint",
        }));
        // Merge SharePoint entries with any local session entries not yet in SP
        const merged = [...spEntries];
        const spActions = new Set(spEntries.map(e => e.action + e.timestamp?.substring(0,16)));
        localAuditLog.forEach(local => {
          const key = local.action + (local.timestamp||"").substring(0,16);
          if (!spActions.has(key)) merged.unshift({ ...local, source: "local" });
        });
        setEntries(merged);
        setSpError(false);
      })
      .catch(() => {
        // SharePoint not reachable — show local session log only
        setEntries([...localAuditLog].reverse());
        setSpError(true);
      })
      .finally(() => setLoading(false));
  }

  // Load on mount and whenever tab becomes visible
  useEffect(() => {
    loadLog();
    const onVisible = () => { if (!document.hidden) loadLog(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const modColor = {
    Purge: "var(--primary)", Delegation: "var(--teal)",
    "Exfil audit": "var(--green)", Trace: "var(--purple)",
    Threat: "var(--warn)", "User containment": "var(--red)",
  };
  const filtered = filter === "all" ? entries : entries.filter((e) => e.module === filter);

  return (
    <div className="fade-in">
      <PageHeader
        title="Audit log"
        sub="All actions across all modules. Mirrored to SharePoint (ITSec) and imait@theimagroup.com."
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" small onClick={loadLog}>↻ Refresh</Btn>
            <Btn variant="ghost" small onClick={() => exportAuditLogCSV(entries)}>Export CSV</Btn>
            <Btn variant="ghost" small onClick={() => exportAuditLogPDF(entries)}>Export PDF</Btn>
          </div>
        }
      />

      {spError && (
        <StatusBar type="warn">
          SharePoint list not yet created or not reachable — showing session log only. Run a purge first to auto-create the list.
        </StatusBar>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {["all", "Purge", "Delegation", "Exfil audit", "Trace", "Threat", "User containment"].map((m) => (
          <Btn key={m} variant="ghost" small onClick={() => setFilter(m)}
            style={{
              borderColor: filter === m ? "var(--primary)" : undefined,
              color: filter === m ? "var(--primary)" : undefined,
            }}>
            {m}
          </Btn>
        ))}
      </div>

      <Card noPad>
        {loading
          ? <div style={{ padding: 24, textAlign: "center", color: "var(--tx3)", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <Spinner size={12}/> Loading from SharePoint…
            </div>
          : filtered.length === 0
            ? <div style={{ padding: 30, textAlign: "center", color: "var(--tx3)", fontSize: 12 }}>
                No log entries yet. Actions will appear here after running any operation.
              </div>
            : (
                <Table
                  cols={["Timestamp", "Operator", "Module", "Action", "Ticket", "Matched", "Deleted", "Failed"]}
                  rows={filtered.map((e) => ({
                    cells: [
                      <span style={{ fontFamily:"var(--mono)", fontSize:10 }}>{e.timestamp}</span>,
                      <span style={{ fontSize:11 }}>{e.operatorEmail}</span>,
                      <Badge type="gray" style={{ color: modColor[e.module] || "var(--tx2)" }}>{e.module}</Badge>,
                      <span style={{ fontSize:11 }}>{e.action}</span>,
                      e.ticketRef || "—",
                      e.matchCount ?? "—",
                      e.successCount !== undefined
                        ? <span style={{ color:"var(--green)", fontFamily:"var(--mono)" }}>{e.successCount}</span>
                        : "—",
                      e.failCount
                        ? <span style={{ color:"var(--red)", fontFamily:"var(--mono)" }}>{e.failCount}</span>
                        : "—",
                    ],
                  }))}
                />
              )
        }
      </Card>
    </div>
  );
}

// ─── USER CONTAINMENT ─────────────────────────────────────────────────────────
export function ContainPage({ user, onScanUpdate }) {
  const [email, setEmail] = useState("");
  const [ticketRef, setTicketRef] = useState("");
  const [lookupPhase, setLookupPhase] = useState("idle");
  const [targetUser, setTargetUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [signIns, setSignIns] = useState([]);
  const [actions, setActions] = useState({ sessions: false, mfa: false, disable: false });
  const [actionPhase, setActionPhase] = useState("idle");
  const [results, setResults] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  async function doLookup() {
    if (!email) return;
    setLookupPhase("loading");
    setError("");
    setTargetUser(null);
    setUserOptions([]);
    setSignIns([]);
    try {
      let u = null;
      let multipleMatches = [];

      if (email.includes("@")) {
        // Direct email/UPN lookup
        u = await getUserByEmail(email);
      } else {
        // Partial name search
        const matches = await searchUsersByName(email);
        if (matches.length === 0) {
          setLookupPhase("notfound");
          return;
        }
        if (matches.length === 1) {
          u = matches[0];
        } else {
          // Multiple matches — let user pick
          setUserOptions(matches);
          setLookupPhase("multiple");
          return;
        }
      }

      if (!u) {
        setLookupPhase("notfound");
        return;
      }

      setTargetUser(u);
      // Sign-in activity may be included in user-lookup function response
      if (u.recentSignIns) {
        setSignIns(u.recentSignIns);
      } else {
        getUserSignInActivity(u.id)
          .then(activity => setSignIns(activity?.value || []))
          .catch(() => setSignIns([]));
      }
      setLookupPhase("found");
    } catch (err) {
      setError(err.message);
      setLookupPhase("idle");
    }
  }

  async function doActions() {
    setActionPhase("running");
    setConfirmOpen(false);
    const res = { sessions: null, mfa: null, disable: null, errors: [] };

    if (actions.sessions || actions.disable) {
      // Revoking sessions is prerequisite for everything anyway
      try { res.sessions = await revokeUserSessions(targetUser.id); }
      catch (e) { res.errors.push(`Session revoke failed: ${e.message}`); }
    }

    if (actions.mfa) {
      try { res.mfa = await revokeUserMfaSessions(targetUser.id); }
      catch (e) { res.errors.push(`MFA revoke failed: ${e.message}`); }
    }

    if (actions.disable) {
      try { res.disable = await disableUserAccount(targetUser.id); }
      catch (e) { res.errors.push(`Account disable failed: ${e.message}`); }
    }

    // Update local state to reflect disabled
    if (actions.disable && !res.errors.find(e => e.includes("disable"))) {
      setTargetUser(u => ({ ...u, accountEnabled: false }));
    }

    // Audit
    const performed = [
      actions.sessions && "Revoke active sessions",
      actions.mfa && "Revoke MFA sessions",
      actions.disable && "Disable account",
    ].filter(Boolean);

    const entry = {
      module: "User containment",
      action: `${performed.join(" · ")} — ${targetUser.userPrincipalName}`,
      operator: user?.displayName,
      operatorEmail: user?.mail,
      ticketRef,
      details: { targetUser: targetUser.userPrincipalName, actionsPerformed: performed },
      severity: "high",
      successCount: res.errors.length === 0 ? performed.length : performed.length - res.errors.length,
      failCount: res.errors.length,
    };
    await Promise.allSettled([writeAuditLog(entry), sendAuditEmail(entry)]);

    setResults(res);
    setActionPhase("done");
  }

  async function doReEnable() {
    try {
      await enableUserAccount(targetUser.id);
      setTargetUser(u => ({ ...u, accountEnabled: true }));
      const entry = {
        module: "User containment",
        action: `Account re-enabled — ${targetUser.userPrincipalName}`,
        operator: user?.displayName,
        operatorEmail: user?.mail,
        ticketRef,
        details: { targetUser: targetUser.userPrincipalName },
        severity: "info",
      };
      await Promise.allSettled([writeAuditLog(entry), sendAuditEmail(entry)]);
    } catch (err) {
      setError("Re-enable failed: " + err.message);
    }
  }

  const anySelected = actions.sessions || actions.mfa || actions.disable;
  const isFullContain = actions.sessions && actions.mfa && actions.disable;

  const ActionToggle = ({ id, label, sub, color = "var(--red)" }) => (
    <div
      onClick={() => setActions(a => ({ ...a, [id]: !a[id] }))}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12,
        padding: "12px 14px", borderRadius: "var(--r)", cursor: "pointer",
        border: `1px solid ${actions[id] ? color : "var(--b2)"}`,
        background: actions[id] ? `${color}10` : "var(--bg)",
        transition: "all .15s", marginBottom: 8,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${actions[id] ? color : "var(--b3)"}`,
        background: actions[id] ? color : "transparent", flexShrink: 0, marginTop: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {actions[id] && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
      </div>
      <div>
        <div style={{ fontSize: 12, color: actions[id] ? "var(--tx)" : "var(--tx2)", fontWeight: 500, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--tx3)" }}>{sub}</div>
      </div>
    </div>
  );

  return (
    <div className="fade-in">
      <PageHeader
        title="User containment"
        sub="Revoke active sessions, MFA sessions, and disable user accounts during an active incident."
      />

      {/* Warning banner */}
      <div style={{
        background: "var(--red-dim)", border: "1px solid rgba(224,82,82,.3)",
        borderRadius: "var(--r)", padding: "10px 14px", marginBottom: 14, fontSize: 11, color: "var(--red)",
      }}>
        ⚠ High-impact actions. All operations are logged to SharePoint and emailed to imait@theimagroup.com. Disabled accounts lose access immediately across all Microsoft 365 services.
      </div>

      {/* User lookup */}
      <Card title="Target user lookup">
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@theimagroup.com"
            />
          </div>
          <Btn variant="ghost" onClick={doLookup} disabled={!email || lookupPhase === "loading"}>
            {lookupPhase === "loading" && <Spinner size={10} />}
            {lookupPhase === "loading" ? "Looking up…" : "Look up user"}
          </Btn>
        </div>
        {error && <StatusBar type="err" style={{ marginTop: 10 }}>{error}</StatusBar>}
        {lookupPhase === "notfound" && <StatusBar type="err">No user found for "{email}". Try their full email address for best results.</StatusBar>}
      </Card>

      {/* Multiple matches — let user pick */}
      {lookupPhase === "multiple" && userOptions.length > 0 && (
        <Card title={`${userOptions.length} users found — select one`}>
          {userOptions.map((u, i) => (
            <div
              key={u.id}
              onClick={() => {
                setTargetUser(u);
                setUserOptions([]);
                setLookupPhase("found");
                getUserSignInActivity(u.id).then(a => setSignIns(a?.value || [])).catch(() => setSignIns([]));
              }}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px", borderRadius: "var(--r)",
                border: "1px solid var(--b2)", marginBottom: 6,
                cursor: "pointer", background: "var(--bg)",
                transition: "all .15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--bg)"}
            >
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                background: "var(--primary-dim)", border: "1px solid var(--primary)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, color: "var(--primary)", fontWeight: 600, flexShrink: 0,
              }}>
                {u.displayName?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, color: "var(--tx)", fontWeight: 500 }}>{u.displayName}</div>
                <div style={{ fontSize: 11, color: "var(--tx3)" }}>{u.userPrincipalName} {u.jobTitle ? `· ${u.jobTitle}` : ""}</div>
              </div>
              <Badge type={u.accountEnabled ? "ok" : "fail"} style={{ marginLeft: "auto" }}>
                {u.accountEnabled ? "Active" : "Disabled"}
              </Badge>
            </div>
          ))}
        </Card>
      )}

      {/* User profile card */}
      {lookupPhase === "found" && targetUser && (
        <>
          <Card title="User profile">
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: targetUser.accountEnabled ? "var(--blue-dim)" : "var(--red-dim)",
                border: `2px solid ${targetUser.accountEnabled ? "var(--blue)" : "var(--red)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, color: targetUser.accountEnabled ? "var(--blue)" : "var(--red)",
                fontFamily: "var(--display)", fontWeight: 700, flexShrink: 0,
              }}>
                {targetUser.displayName?.[0]?.toUpperCase() || "?"}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--display)", color: "var(--tx)", marginBottom: 2 }}>{targetUser.displayName}</div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 4 }}>{targetUser.userPrincipalName}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge type={targetUser.accountEnabled ? "ok" : "fail"}>
                    {targetUser.accountEnabled ? "Account active" : "Account disabled"}
                  </Badge>
                  {targetUser.jobTitle && <Badge type="gray">{targetUser.jobTitle}</Badge>}
                  {targetUser.department && <Badge type="gray">{targetUser.department}</Badge>}
                </div>
              </div>
              {!targetUser.accountEnabled && (
                <div style={{ marginLeft: "auto" }}>
                  <Btn variant="green" onClick={doReEnable}>Re-enable account</Btn>
                </div>
              )}
            </div>

            {/* Recent sign-in activity */}
            {signIns.length > 0 && (
              <>
                <div style={{ fontSize: 10, color: "var(--tx3)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>Recent sign-in activity</div>
                <Table
                  cols={["Time", "App", "IP address", "Location", "Status"]}
                  rows={signIns.slice(0, 5).map(s => ({
                    cells: [
                      new Date(s.createdDateTime).toLocaleString(),
                      s.appDisplayName || "—",
                      s.ipAddress || "—",
                      s.location?.city ? `${s.location.city}, ${s.location.countryOrRegion}` : "—",
                      <Badge type={s.status?.errorCode === 0 ? "ok" : "fail"}>
                        {s.status?.errorCode === 0 ? "Success" : "Failed"}
                      </Badge>,
                    ],
                  }))}
                />
              </>
            )}
          </Card>

          {/* Action selection */}
          <Card title="Select containment actions">
            <div style={{ marginBottom: 10 }}>
              <ActionToggle
                id="sessions"
                label="Revoke all active sessions"
                sub="Immediately invalidates all active sign-in tokens across all devices and apps. User must re-authenticate everywhere."
                color="var(--warn)"
              />
              <ActionToggle
                id="mfa"
                label="Revoke MFA / authenticator sessions"
                sub="Invalidates refresh tokens tied to MFA claims. Forces full re-authentication including MFA on next sign-in."
                color="var(--warn)"
              />
              <ActionToggle
                id="disable"
                label="Disable account"
                sub="Blocks all sign-ins immediately across M365. Mailbox, files, and data are preserved. Can be re-enabled at any time."
                color="var(--red)"
              />
            </div>

            {/* Quick-select all */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <Btn variant="ghost" small onClick={() => setActions({ sessions: true, mfa: true, disable: true })}>
                Select all (full containment)
              </Btn>
              <Btn variant="ghost" small onClick={() => setActions({ sessions: false, mfa: false, disable: false })}>
                Clear
              </Btn>
            </div>

            <Field label="HaloITSM ticket ref (optional)">
              <Input value={ticketRef} onChange={e => setTicketRef(e.target.value)} placeholder="e.g. INC-2024-047" />
            </Field>

            <div style={{ marginTop: 14 }}>
              <Btn
                variant="danger"
                disabled={!anySelected || actionPhase === "running"}
                onClick={() => setConfirmOpen(true)}
              >
                {actionPhase === "running" && <Spinner size={10} />}
                {actionPhase === "running"
                  ? "Applying…"
                  : isFullContain
                  ? `Full containment — ${targetUser.displayName}`
                  : `Apply ${[actions.sessions && "session revoke", actions.mfa && "MFA revoke", actions.disable && "disable"].filter(Boolean).length} action(s)`}
              </Btn>
            </div>
          </Card>

          {/* Confirm dialog */}
          {confirmOpen && (
            <div style={{
              background: "var(--red-dim)", border: "1px solid rgba(224,82,82,.35)",
              borderRadius: "var(--r)", padding: "16px", marginBottom: 14,
            }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--red)", marginBottom: 8 }}>
                Confirm containment actions for {targetUser.displayName}
              </div>
              <div style={{ fontSize: 11, color: "var(--tx2)", marginBottom: 4 }}>The following will be applied immediately:</div>
              {[
                actions.sessions && "✓ All active sessions will be revoked",
                actions.mfa && "✓ All MFA sessions will be revoked",
                actions.disable && "✓ Account will be disabled — all M365 access blocked",
              ].filter(Boolean).map((a, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--tx2)", padding: "3px 0" }}>{a}</div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <Btn variant="danger" onClick={doActions}>Confirm — apply now</Btn>
                <Btn variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* Results */}
          {actionPhase === "done" && (
            <Card title="Containment results">
              {[
                actions.sessions && { label: "Active sessions", ok: !results.errors?.find(e => e.includes("Session")), detail: "All sign-in tokens invalidated across all devices" },
                actions.mfa && { label: "MFA sessions", ok: !results.errors?.find(e => e.includes("MFA")), detail: "MFA refresh tokens revoked — full re-auth required" },
                actions.disable && { label: "Account disabled", ok: !results.errors?.find(e => e.includes("disable")), detail: "Account blocked — user cannot sign in to any M365 service" },
              ].filter(Boolean).map((r, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 0", borderBottom: "1px solid var(--b1)",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "var(--r)", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: r.ok ? "var(--green-dim)" : "var(--red-dim)",
                    fontSize: 12, color: r.ok ? "var(--green)" : "var(--red)",
                  }}>
                    {r.ok ? "✓" : "✗"}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--tx)", fontWeight: 500, marginBottom: 2 }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: "var(--tx3)" }}>{r.detail}</div>
                  </div>
                  <Badge type={r.ok ? "ok" : "fail"} style={{ marginLeft: "auto" }}>
                    {r.ok ? "applied" : "failed"}
                  </Badge>
                </div>
              ))}

              {results.errors?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {results.errors.map((e, i) => (
                    <StatusBar key={i} type="err">{e}</StatusBar>
                  ))}
                </div>
              )}

              <StatusBar type={results.errors?.length ? "warn" : "ok"} style={{ marginTop: 12 }}>
                Actions logged to SharePoint (ITSec) and emailed to imait@theimagroup.com
                {ticketRef && ` · Ticket: ${ticketRef}`}
              </StatusBar>

              <BtnRow>
                {actions.disable && targetUser && (
                  <Btn variant="ghost" onClick={doReEnable}>Re-enable account when ready</Btn>
                )}
                <Btn variant="ghost" onClick={() => { setActionPhase("idle"); setActions({ sessions: false, mfa: false, disable: false }); setConfirmOpen(false); }}>
                  New containment
                </Btn>
              </BtnRow>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
